import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronRight, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Camper-side "Events at this campsite" strip.
// Fetches GET /api/events/campsite/{id}. If the viewer hasn't visited
// (backend gates on 403 for non-owners without a validated check-in), the
// strip stays hidden. Owners see it always, campers see only current /
// upcoming events + past events they participated in.
export default function CampsiteEventStrip({ campsiteId, isOwnCampsite, onCreate }) {
  const { api } = useAuth();
  const [items, setItems] = useState(null);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/events/campsite/${campsiteId}`)
      .then(({ data }) => { if (!cancelled) setItems(Array.isArray(data) ? data : (data?.items || [])); })
      .catch((e) => {
        if (cancelled) return;
        // 403 = camper hasn't visited yet → don't show the strip at all.
        // Anything else → also hide silently; events are non-critical.
        if (e?.response?.status === 403) setGated(true);
        setItems([]);
      });
    return () => { cancelled = true; };
  }, [api, campsiteId]);

  // Hide entirely if the caller is a not-yet-visited camper AND owner
  // authoring isn't offered here.
  if (gated && !isOwnCampsite) return null;
  if (items == null) return null;
  if (items.length === 0 && !isOwnCampsite) return null;

  return (
    <section className="card-elev p-5 flex flex-col gap-3" data-testid="campsite-events-strip">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: "var(--premium)" }} />
          <h2 className="text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>
            Events {items.length > 0 && <span className="text-sm font-bold" style={{ color: "var(--on-surface-muted)" }}>· {items.length}</span>}
          </h2>
        </div>
        {isOwnCampsite && (
          <Link to={`/owner/events/new?campsite=${campsiteId}`}
                className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
                style={{ color: "var(--brand)" }}
                data-testid="campsite-events-new">
            + New event
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--on-surface-muted)" }} data-testid="campsite-events-empty">
          No events yet. Create your first one to reward campers with bonus XP.
        </div>
      ) : (
        <div className="flex flex-col divide-y" style={{ borderColor: "var(--border-c)" }} data-testid="campsite-events-list">
          {items.map((ev) => <EventRow key={ev.event_id} ev={ev} isOwnCampsite={isOwnCampsite} />)}
        </div>
      )}
    </section>
  );
}

function EventRow({ ev, isOwnCampsite }) {
  const start = (ev.start_at || "").slice(0, 10);
  const status = ev.status || "upcoming";
  const statusColor = status === "active" ? "var(--brand)" : status === "upcoming" ? "var(--premium)" : "var(--on-surface-muted)";
  const to = isOwnCampsite ? `/owner/events/${ev.event_id}` : null;
  const Wrap = to ? Link : "div";
  const wrapProps = to ? { to } : {};
  return (
    <Wrap {...wrapProps}
          className="py-3 flex items-center gap-3 hover:opacity-80"
          data-testid={`campsite-event-${ev.event_id}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: "color-mix(in srgb, var(--premium) 15%, transparent)", color: "var(--premium)" }}>
        <CalendarDays className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-extrabold truncate" style={{ color: "var(--on-surface)" }}>{ev.name}</div>
          <span className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0"
                style={{ background: "color-mix(in srgb, currentColor 15%, transparent)", color: statusColor }}>
            {status}
          </span>
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: "var(--on-surface-muted)" }}>
          Starts {start} · {ev.tasks_count ?? (ev.tasks?.length ?? 0)} tasks · {ev.joined_count ?? 0} joined
        </div>
      </div>
      {to && <ChevronRight className="w-4 h-4 opacity-40" />}
    </Wrap>
  );
}
